import { Box, Skeleton } from '@factorialco/deprecated-design-system'

import SelectField from 'modules/core/components/SelectField'
import i18n from 'modules/core/lib/i18n'

import { AssignmentPicker } from 'modules/cost_centers/components/CostCenterAllocations/AssignmentPicker'
import { ReferenceAssignmentTags } from 'modules/cost_centers/components/CostCenterAllocations/ReferenceAssignmentTags'
import { useCostCenterAllocation } from 'modules/cost_centers/hooks/useCostCenterAllocation'
import { useCostCenterOptions } from 'modules/cost_centers/hooks/useCostCenterDistributionOptions'
import {
  AllocationOption,
  AllocationReference,
  AssignmentsChange,
  ResourceDistribution,
} from 'modules/cost_centers/lib/types'

interface Props {
  resourceType: string
  resourceId?: string
  mandatory?: boolean
  readonly?: boolean
  defaultSelectOption?: string
  resourceDate: string
  onChange?: (
    value: { assignments: AssignmentsChange } | { allocation: AllocationReference }
  ) => void
  options?: AllocationOption[]
  distribution?: ResourceDistribution
  referenceResourceId?: string
}

export const CostCenterAllocations = (props: Props) => {
  const {
    resourceType,
    resourceId,
    mandatory = true,
    readonly = false,
    defaultSelectOption,
    onChange,
    resourceDate,
    options: optionsFromResource,
    distribution,
    referenceResourceId,
  } = props

  const { options, loading: loadingOptions } = useCostCenterOptions(
    resourceDate,
    resourceId,
    resourceType,
    optionsFromResource
  )
  const hasOptions = options.length > 0
  const onlyResourceAsOption = options.length === 1 && options[0]?.value === resourceType

  const {
    selectValue,
    selectedOption,
    selectedOptionIsResource,
    resourceDistribution,
    loadingResourceDistribution,
    handleSelectChange,
  } = useCostCenterAllocation({
    resourceType,
    resourceId,
    referenceResourceId,
    defaultSelectOption,
    options,
    onChange,
    distribution,
    loading: loadingOptions,
  })

  if (loadingResourceDistribution || loadingOptions) {
    return <Skeleton width="full" height="s32" />
  }

  if (!hasOptions) return null

  if (onlyResourceAsOption) {
    return (
      <AssignmentPicker
        resourceDistribution={resourceDistribution}
        resourceDate={resourceDate}
        onChange={onChange}
        readonly={readonly}
      />
    )
  }

  return (
    <Box gap="s8">
      <SelectField
        label={i18n.t('cost_centers.select_title')}
        mandatory={mandatory}
        options={options}
        value={selectValue}
        onChange={handleSelectChange}
        readonly={readonly}
      />

      {selectValue !== resourceType && (
        <ReferenceAssignmentTags
          selectedOption={selectedOption}
          resourceDate={resourceDate}
          selectedOptionIsResource={selectedOptionIsResource}
        />
      )}

      {selectValue === resourceType && (
        <AssignmentPicker
          resourceDistribution={resourceDistribution}
          resourceDate={resourceDate}
          onChange={onChange}
          readonly={readonly}
        />
      )}
    </Box>
  )
}
